import { useState, useRef, useEffect } from 'react';
import { useStudioStore } from '../../store/useStudioStore';
import { Scissors, Check, X, Play, Pause } from 'lucide-react';

export const CompingEditor = () => {
  const {
    recordingTakes,
    compRegions,
    setCompRegions,
  } = useStudioStore();

  const [selectedRegions, setSelectedRegions] = useState<Map<string, { start: number; end: number }>>(new Map());
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{ takeId: string; position: number } | null>(null);
  const [isCreatingComposite, setIsCreatingComposite] = useState(false);
  const [playingTakeId, setPlayingTakeId] = useState<string | null>(null);
  
  const audioRefs = useRef<Map<string, HTMLAudioElement>>(new Map());
  const canvasRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());

  // Draw waveform on canvas
  useEffect(() => {
    recordingTakes.forEach((take) => {
      const canvas = canvasRefs.current.get(take.id);
      if (!canvas || !take.waveform.length) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const width = canvas.width;
      const height = canvas.height;
      const barWidth = width / take.waveform.length;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Draw waveform
      ctx.fillStyle = '#3b82f6';
      take.waveform.forEach((value, index) => {
        const barHeight = value * height;
        const x = index * barWidth;
        const y = (height - barHeight) / 2;
        ctx.fillRect(x, y, barWidth - 1, barHeight);
      });

      // Draw selected regions
      const region = selectedRegions.get(take.id);
      if (region) {
        ctx.fillStyle = 'rgba(34, 197, 94, 0.3)';
        const startX = (region.start / take.duration) * width;
        const endX = (region.end / take.duration) * width;
        ctx.fillRect(startX, 0, endX - startX, height);

        // Draw region borders
        ctx.strokeStyle = '#22c55e';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(startX, 0);
        ctx.lineTo(startX, height);
        ctx.moveTo(endX, 0);
        ctx.lineTo(endX, height);
        ctx.stroke();
      }
    });
  }, [recordingTakes, selectedRegions]);

  // Handle mouse down on waveform
  const handleMouseDown = (takeId: string, event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRefs.current.get(takeId);
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const take = recordingTakes.find((t) => t.id === takeId);
    if (!take) return;

    const position = (x / canvas.width) * take.duration;
    
    setIsSelecting(true);
    setSelectionStart({ takeId, position });
  };

  // Handle mouse move on waveform
  const handleMouseMove = (takeId: string, event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isSelecting || !selectionStart || selectionStart.takeId !== takeId) return;

    const canvas = canvasRefs.current.get(takeId);
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const take = recordingTakes.find((t) => t.id === takeId);
    if (!take) return;

    const position = (x / canvas.width) * take.duration;
    const start = Math.min(selectionStart.position, position);
    const end = Math.max(selectionStart.position, position);

    setSelectedRegions((prev) => {
      const newMap = new Map(prev);
      newMap.set(takeId, { start, end });
      return newMap;
    });
  };

  // Handle mouse up
  const handleMouseUp = () => {
    setIsSelecting(false);
    setSelectionStart(null);
  };

  // Clear selection for a take
  const clearSelection = (takeId: string) => {
    setSelectedRegions((prev) => {
      const newMap = new Map(prev);
      newMap.delete(takeId);
      return newMap;
    });
  };

  // Clear all selections
  const clearAllSelections = () => {
    setSelectedRegions(new Map());
  };

  // Play/pause take
  const togglePlayTake = (takeId: string) => {
    const audio = audioRefs.current.get(takeId);
    if (!audio) return;

    if (playingTakeId === takeId) {
      audio.pause();
      audio.currentTime = 0;
      setPlayingTakeId(null);
    } else {
      // Pause all other takes
      audioRefs.current.forEach((otherAudio, otherId) => {
        if (otherId !== takeId) {
          otherAudio.pause();
          otherAudio.currentTime = 0;
        }
      });

      audio.play();
      setPlayingTakeId(takeId);
    }
  };

  // Handle audio ended
  const handleAudioEnded = (takeId: string) => {
    if (playingTakeId === takeId) {
      setPlayingTakeId(null);
    }
  };

  // Create composite from selected regions
  const createComposite = async () => {
    if (selectedRegions.size === 0) {
      alert('Please select regions from at least one take');
      return;
    }

    setIsCreatingComposite(true);

    try {
      // Convert selected regions to CompRegion format
      const regions: Array<{ takeId: string; startTime: number; endTime: number }> = [];
      selectedRegions.forEach((region, takeId) => {
        regions.push({
          takeId,
          startTime: region.start,
          endTime: region.end,
        });
      });

      // Sort regions by start time
      regions.sort((a, b) => a.startTime - b.startTime);

      // Create audio context for mixing
      const audioContext = new AudioContext();
      
      // Calculate total duration
      const totalDuration = Math.max(...regions.map((r) => r.endTime));
      const sampleRate = audioContext.sampleRate;
      const totalSamples = Math.ceil(totalDuration * sampleRate);

      // Create composite buffer
      const composite = audioContext.createBuffer(2, totalSamples, sampleRate);
      const leftChannel = composite.getChannelData(0);
      const rightChannel = composite.getChannelData(1);

      // Mix selected regions
      for (const region of regions) {
        const take = recordingTakes.find((t) => t.id === region.takeId);
        if (!take) continue;

        // Load audio buffer
        const response = await fetch(take.audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        // Calculate sample positions
        const startSample = Math.floor(region.startTime * sampleRate);
        const endSample = Math.floor(region.endTime * sampleRate);
        const regionLength = endSample - startSample;

        // Copy samples from source to composite
        const sourceLeft = audioBuffer.getChannelData(0);
        const sourceRight = audioBuffer.numberOfChannels > 1 ? audioBuffer.getChannelData(1) : sourceLeft;

        for (let i = 0; i < regionLength && startSample + i < totalSamples; i++) {
          if (i < sourceLeft.length) {
            leftChannel[startSample + i] = sourceLeft[i];
            rightChannel[startSample + i] = sourceRight[i];
          }
        }
      }

      // Convert to WAV
      const wav = audioBufferToWav(composite);
      const blob = new Blob([wav], { type: 'audio/wav' });
      const url = URL.createObjectURL(blob);

      // Create new take from composite
      const compositeTake = {
        trackId: recordingTakes[0]?.trackId || 'default',
        audioUrl: url,
        startTime: 0,
        duration: totalDuration,
        waveform: await generateWaveform(blob),
        selected: false,
        timestamp: Date.now(),
      };

      // Add composite take to store
      useStudioStore.getState().addRecordingTake(compositeTake);

      // Clear selections
      clearAllSelections();

      alert('Composite created successfully!');
    } catch (error) {
      console.error('Failed to create composite:', error);
      alert('Failed to create composite. Please try again.');
    } finally {
      setIsCreatingComposite(false);
    }
  };

  // Convert AudioBuffer to WAV
  const audioBufferToWav = (buffer: AudioBuffer): ArrayBuffer => {
    const length = buffer.length * buffer.numberOfChannels * 2;
    const arrayBuffer = new ArrayBuffer(44 + length);
    const view = new DataView(arrayBuffer);

    // Write WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + length, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, buffer.numberOfChannels, true);
    view.setUint32(24, buffer.sampleRate, true);
    view.setUint32(28, buffer.sampleRate * buffer.numberOfChannels * 2, true);
    view.setUint16(32, buffer.numberOfChannels * 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, length, true);

    // Write audio data
    const channels = [];
    for (let i = 0; i < buffer.numberOfChannels; i++) {
      channels.push(buffer.getChannelData(i));
    }

    let offset = 44;
    for (let i = 0; i < buffer.length; i++) {
      for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, channels[channel][i]));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
        offset += 2;
      }
    }

    return arrayBuffer;
  };

  // Generate waveform from blob
  const generateWaveform = async (blob: Blob): Promise<number[]> => {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const audioContext = new AudioContext();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      const channelData = audioBuffer.getChannelData(0);
      const samples = 100;
      const blockSize = Math.floor(channelData.length / samples);
      const waveform: number[] = [];

      for (let i = 0; i < samples; i++) {
        const start = i * blockSize;
        const end = start + blockSize;
        let sum = 0;
        
        for (let j = start; j < end; j++) {
          sum += Math.abs(channelData[j]);
        }
        
        waveform.push(sum / blockSize);
      }

      audioContext.close();
      return waveform;
    } catch (err) {
      console.error('Failed to generate waveform:', err);
      return Array(100).fill(0);
    }
  };

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (recordingTakes.length === 0) {
    return (
      <div className="p-6">
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-8 text-center">
          <Scissors className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Takes Available</h3>
          <p className="text-zinc-400">
            Record some takes first to use the comping editor
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-white mb-2">Comping Editor</h3>
          <p className="text-sm text-zinc-400">
            Select regions from different takes to create a perfect composite
          </p>
        </div>

        <div className="flex items-center gap-3">
          {selectedRegions.size > 0 && (
            <>
              <button
                onClick={clearAllSelections}
                className="flex items-center gap-2 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                Clear All
              </button>
              <button
                onClick={createComposite}
                disabled={isCreatingComposite}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg font-medium transition-colors"
              >
                <Check className="w-4 h-4" />
                {isCreatingComposite ? 'Creating...' : 'Create Composite'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
        <p className="text-sm text-blue-300">
          <strong>How to use:</strong> Click and drag on a waveform to select a region. 
          You can select regions from multiple takes. Click "Create Composite" to merge selected regions into a new take.
        </p>
      </div>

      {/* Take Lanes */}
      <div className="space-y-4">
        {recordingTakes.map((take, index) => {
          const region = selectedRegions.get(take.id);
          const isPlaying = playingTakeId === take.id;

          return (
            <div
              key={take.id}
              className="bg-zinc-800 border border-zinc-700 rounded-lg p-4"
            >
              {/* Take Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-white font-medium">
                    Take {recordingTakes.length - index}
                  </span>
                  <span className="text-sm text-zinc-400">
                    {formatTime(take.duration)}
                  </span>
                  {region && (
                    <span className="text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded">
                      {formatTime(region.start)} - {formatTime(region.end)}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => togglePlayTake(take.id)}
                    className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
                    title={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isPlaying ? (
                      <Pause className="w-4 h-4 text-white" />
                    ) : (
                      <Play className="w-4 h-4 text-white" />
                    )}
                  </button>
                  {region && (
                    <button
                      onClick={() => clearSelection(take.id)}
                      className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Clear selection"
                    >
                      <X className="w-4 h-4 text-red-400" />
                    </button>
                  )}
                </div>
              </div>

              {/* Waveform Canvas */}
              <canvas
                ref={(el) => {
                  if (el) canvasRefs.current.set(take.id, el);
                }}
                width={800}
                height={80}
                className="w-full h-20 bg-zinc-900 rounded cursor-crosshair"
                onMouseDown={(e) => handleMouseDown(take.id, e)}
                onMouseMove={(e) => handleMouseMove(take.id, e)}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              />

              {/* Hidden audio element */}
              <audio
                ref={(el) => {
                  if (el) audioRefs.current.set(take.id, el);
                }}
                src={take.audioUrl}
                onEnded={() => handleAudioEnded(take.id)}
                className="hidden"
              />
            </div>
          );
        })}
      </div>

      {/* Selected Regions Summary */}
      {selectedRegions.size > 0 && (
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-white mb-2">
            Selected Regions ({selectedRegions.size})
          </h4>
          <div className="space-y-1">
            {Array.from(selectedRegions.entries()).map(([takeId, region]) => {
              const take = recordingTakes.find((t) => t.id === takeId);
              const takeIndex = recordingTakes.findIndex((t) => t.id === takeId);
              if (!take) return null;

              return (
                <div key={takeId} className="flex items-center justify-between text-sm">
                  <span className="text-zinc-300">
                    Take {recordingTakes.length - takeIndex}
                  </span>
                  <span className="text-zinc-400">
                    {formatTime(region.start)} - {formatTime(region.end)} 
                    ({formatTime(region.end - region.start)})
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
